import React from 'react';
import InfoIcon from 'assets/info.svg?react';
import ErrorCircleOutlineIcon from 'assets/error-circle-outline.svg?react';
import {
  WEATHER_ERROR_TYPES,
  WeatherErrorType
} from '../../../Helpers/weatherErrorModal';

export interface WeatherBannerConfig {
  showBanner: boolean;
  icon: React.FC<React.SVGProps<SVGSVGElement>> | null;
  iconColor: string;
  backgroundColor: string;
  borderColor: string;
  textColor: string;
  message: React.ReactNode;
}

interface UseWeatherBannerParams {
  areDatesInFuture: boolean;
  isAutoWeatherEnabled: boolean;
  apiErrorValue: WeatherErrorType | null;
}

const BANNER_MESSAGES = {
  CENTROID_ERROR: () => (
    <>
      We&apos;re having difficulty calculating the center point between your
      selected fields. To learn more about how we provide automatic weather
      data,{' '}
      <a
        href="https://www.syngentadigital.com/us-en#support"
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: '#0092E4', textDecoration: 'underline' }}
      >
        visit our support center
      </a>
      {'. '}You can add weather information manually, or save without weather.
    </>
  ),
  WEATHER_API_ERROR:
    'An error prevented us from getting your weather data at this time. You can add weather information manually, or try again later.',
  FUTURE_DATES:
    'Weather data cannot be added to Applications that occur in the future. You can add weather information manually, or edit this record after the End Date & Time has occurred to populate automatic weather data.'
};

/**
 * Hook to manage which weather banner to display based on conditions
 * Returns banner configuration values for FormSection
 */
const useWeatherBanner = ({
  areDatesInFuture,
  isAutoWeatherEnabled,
  apiErrorValue
}: UseWeatherBannerParams): WeatherBannerConfig => {
  // Priority 1: API Error banner (centroid calculation failed)
  if (
    apiErrorValue === WEATHER_ERROR_TYPES.CALC_CENTROID_ERROR &&
    isAutoWeatherEnabled
  ) {
    return {
      showBanner: true,
      icon: ErrorCircleOutlineIcon,
      iconColor: '#EB4B4B',
      backgroundColor: '#FFF1F1',
      borderColor: '#F98989',
      textColor: '#14151C',
      message: BANNER_MESSAGES.CENTROID_ERROR()
    };
  }

  // Priority 2: API Error banner (weather API failed)
  if (
    apiErrorValue === WEATHER_ERROR_TYPES.WEATHER_API_ERROR &&
    isAutoWeatherEnabled
  ) {
    return {
      showBanner: true,
      icon: ErrorCircleOutlineIcon,
      iconColor: '#EB4B4B',
      backgroundColor: '#FFF1F1',
      borderColor: '#F98989',
      textColor: '#14151C',
      message: BANNER_MESSAGES.WEATHER_API_ERROR
    };
  }

  // Priority 3: Future dates banner
  if (areDatesInFuture && isAutoWeatherEnabled) {
    return {
      showBanner: true,
      icon: InfoIcon,
      iconColor: '#0092E4',
      backgroundColor: '#EAF6FF',
      borderColor: '#82CFFF',
      textColor: '#14151C',
      message: BANNER_MESSAGES.FUTURE_DATES
    };
  }

  // No banner to show
  return {
    showBanner: false,
    icon: null,
    iconColor: '',
    backgroundColor: '',
    borderColor: '',
    textColor: '',
    message: null
  };
};

export default useWeatherBanner;
